import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, X, FileText, VideoIcon, ImageIcon } from 'lucide-react'; // Added VideoIcon, ImageIcon, FileText
import { useFile } from '../../services/FileService';
import { isRenderableImage, isPlayableVideo } from '../../utils/media-types';
import './CaseGallery.scss';

// Helper component to render media (image or video) with a signed URL
const SignedMediaItem = ({ item, className, onClick, isThumbnail = false, altText }) => {
    const { signedUrl, loading, error } = useFile(item.url);
    // Only formats the browser can actually draw or play — a TIFF or an AVI is
    // an attachment, and rendering one gives a broken tile. See utils/media-types.
    const isImage = isRenderableImage(item.contentType);
    const isVideo = isPlayableVideo(item.contentType);
    const defaultAlt = isImage ? 'תמונה' : isVideo ? 'וידאו' : 'קובץ';
    
    // Process filename to get the base name
    const baseFilename = item.filename ? item.filename.substring(item.filename.lastIndexOf('/') + 1) : '';
    const itemTitle = baseFilename || defaultAlt; // Use baseFilename for the title attribute

    if (loading) {
        return <div className={`signed-media-placeholder ${className || ''}`} title={`טוען ${defaultAlt}...`}></div>;
    }

    if (error) {
        console.error("Error loading signed URL for media:", item.url, error);
        return <div className={`signed-media-error ${className || ''}`} title={`שגיאה בטעינת ${defaultAlt}`}>⚠️</div>;
    }

    if (isImage) {
        return (
            <img
                src={signedUrl}
                alt={altText || baseFilename || 'תמונה'} // Use baseFilename for alt text
                className={`${className || ''} gallery-image`}
                onClick={onClick}
                title={itemTitle}
            />
        );
    } else if (isVideo) {
        if (isThumbnail) {
            // For thumbnails, show an icon instead of a video player
            return (
                <div className={`${className || ''} gallery-video-thumbnail`} onClick={onClick} title={itemTitle}>
                    <VideoIcon size={32} />
                    {baseFilename && <span className="gallery-thumbnail-filename">{baseFilename}</span>} {/* Use baseFilename for display */}
                </div>
            );
        }
        return (
            <video
                src={signedUrl}
                controls
                className={`${className || ''} gallery-video`}
                title={itemTitle}
                onError={(e) => {
                    // Fallback for video errors
                    e.target.style.display = 'none';
                    const parent = e.target.parentNode;
                    if (parent) {
                        const errorMsg = document.createElement('div');
                        errorMsg.className = 'gallery-video-error-fallback';
                        errorMsg.textContent = 'לא ניתן לטעון את הוידאו.';
                        parent.appendChild(errorMsg);
                    }
                }}
            >
                הדפדפן שלך אינו תומך בתגית הווידאו. <a href={signedUrl} target="_blank" rel="noopener noreferrer">הורד את הקובץ</a>
            </video>
        );
    } else {
        // Fallback for other file types (e.g., documents) as thumbnails
        return (
            <div className={`${className || ''} gallery-document-thumbnail`} onClick={onClick} title={itemTitle}>
                <FileText size={isThumbnail ? 32 : 48} />
                {baseFilename && <span className="gallery-thumbnail-filename">{baseFilename}</span>} {/* Use baseFilename for display */}
            </div>
        );
    }
};

SignedMediaItem.propTypes = {
    item: PropTypes.shape({
        url: PropTypes.string.isRequired,
        filename: PropTypes.string,
        contentType: PropTypes.string, // contentType is important for determining media type
    }).isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
    isThumbnail: PropTypes.bool,
    altText: PropTypes.string,
};


const CaseGallery = ({ mediaItems = [], initialIndex = 0, onClose, title = '', description = '' }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? mediaItems.length - 1 : prevIndex - 1
        );
    }, [mediaItems.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === mediaItems.length - 1 ? 0 : prevIndex + 1
        );
    }, [mediaItems.length]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            } else if (event.key === 'ArrowLeft') {
                handlePrevious();
            } else if (event.key === 'ArrowRight') {
                handleNext();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, handlePrevious, handleNext]);

    if (!mediaItems || mediaItems.length === 0) {
        return null;
    }

    const currentMediaItem = mediaItems[currentIndex];
    const showNavigation = mediaItems.length > 1;

    // Determine if current item is a video to prevent next on click for videos
    const currentItemIsVideo = isPlayableVideo(currentMediaItem.contentType);


    return (
        <div className="case-gallery-overlay" role="dialog" aria-modal="true" aria-labelledby="gallery-title">
            <button className="gallery-close-button" onClick={onClose} aria-label="סגור גלריה">
                <X size={32} />
            </button>

            <div className="gallery-content-wrapper">
                {showNavigation && (
                    <button className="gallery-nav-button prev" onClick={handlePrevious} aria-label="פריט קודם">
                        <ChevronRight size={40} /> {/* ChevronRight for RTL previous */}
                    </button>
                )}

                 <div className="gallery-media-container"
                    // Only allow click-to-next for non-video items if there's more than one item
                    onClick={!currentItemIsVideo && mediaItems.length > 1 ? handleNext : undefined}
                 >
                     <SignedMediaItem
                         item={currentMediaItem}
                         altText={currentMediaItem.filename || `פריט ${currentIndex + 1}`}
                         className="gallery-main-media"
                     />
                      <div className="gallery-media-info">
                         {title && <h4 id="gallery-title" className="gallery-media-title">{title}</h4>}
                         {description && <p className="gallery-media-description">{description}</p>}
                     </div>
                 </div>

                {showNavigation && (
                    <button className="gallery-nav-button next" onClick={handleNext} aria-label="פריט הבא">
                        <ChevronLeft size={40} /> {/* ChevronLeft for RTL next */}
                    </button>
                )}
            </div>

            {showNavigation && (
                <div className="gallery-thumbnails-container">
                    {mediaItems.map((item, index) => (
                        <SignedMediaItem
                            key={item._id || item.url || index} // Ensure a unique key
                            item={item}
                            isThumbnail={true}
                            className={`gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                            altText={`פריט ממוזער ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

CaseGallery.propTypes = {
    mediaItems: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.string,
        url: PropTypes.string.isRequired,
        filename: PropTypes.string,
        contentType: PropTypes.string, // Essential for determining how to render
    })).isRequired,
    initialIndex: PropTypes.number,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
};

export default CaseGallery;
